// Microphone capture for the voice turn: hold to talk, release to send.

import { LevelMeter, MIC_LEVEL_OPTIONS, unlockAudio } from "./level";

export type MicErrorCode =
  | "unsupported"  // the browser cannot record at all
  | "denied"       // the person said no, or the page is not on a secure origin
  | "unavailable"; // no device, or another app holds it

/**
 * `message` is for the console, not the screen: user-facing text is keyed by
 * locale in copy.ts, so the UI switches on `code`.
 */
export class MicError extends Error {
  readonly code: MicErrorCode;

  constructor(code: MicErrorCode, message: string) {
    super(message);
    this.name = "MicError";
    this.code = code;
  }
}

// In preference order. Opus in WebM is what gpt-4o-transcribe is happiest with;
// Safari only offers MP4/AAC, and older Firefox only Ogg.
const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
  "audio/ogg",
];

/** Shorter than this and it was a mis-tap, not a sentence. */
const MIN_DURATION_MS = 400;

/**
 * Peak of the smoothed level below which nothing was said. Low enough that a
 * quiet, close-mic sentence still counts — a false negative here loses the
 * person's words, a false positive only costs one transcription request.
 */
const MIN_PEAK_LEVEL = 0.06;

/**
 * Returns the best supported container, `""` when the browser gives us no way
 * to ask (take its default), or null when it cannot record at all.
 */
function pickMimeType(): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  if (typeof MediaRecorder.isTypeSupported !== "function") return "";
  for (const candidate of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate;
  }
  return "";
}

export class MicRecorder {
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private meter: LevelMeter | null = null;
  private chunks: Blob[] = [];
  private startedAtMs = 0;
  private pending: Promise<Blob | null> | null = null;
  private chosenMime = "";

  /** Whether this browser can capture audio at all. Check before offering it. */
  static get supported(): boolean {
    return (
      typeof window !== "undefined" &&
      typeof navigator !== "undefined" &&
      !!navigator.mediaDevices?.getUserMedia &&
      pickMimeType() !== null
    );
  }

  /**
   * Call from the first user interaction. Browsers start the AudioContext
   * suspended, and a suspended context yields a dead level signal.
   */
  static async unlock(): Promise<void> {
    await unlockAudio();
  }

  /** 0..1, smoothed. Zero whenever nothing is being recorded. */
  get level(): number {
    return this.meter?.level ?? 0;
  }

  /** The container actually in use, so the upload can name the file. */
  get mimeType(): string {
    return this.recorder?.mimeType || this.chosenMime || "audio/webm";
  }

  get recording(): boolean {
    return this.recorder?.state === "recording";
  }

  async start(): Promise<void> {
    if (this.recorder) return;
    const mime = pickMimeType();
    if (mime === null || !navigator.mediaDevices?.getUserMedia) {
      throw new MicError("unsupported", "MediaRecorder is not available in this browser");
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true, // so the blob does not react to our own voice
          noiseSuppression: true,
          // AGC normalises loudness, which flattens the amplitude envelope and
          // makes the blob pump uniformly instead of tracking what is said.
          autoGainControl: false,
          channelCount: 1,
        },
      });
    } catch (err) {
      throw new MicError(deviceErrorCode(err), `getUserMedia failed: ${describe(err)}`);
    }

    try {
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      this.chosenMime = mime;
      this.chunks = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) this.chunks.push(event.data);
      };
      this.recorder = recorder;
      this.stream = stream;
      this.startedAtMs = performance.now();
      recorder.start();
    } catch (err) {
      releaseStream(stream);
      this.recorder = null;
      this.stream = null;
      throw new MicError("unavailable", `MediaRecorder refused to start: ${describe(err)}`);
    }

    // The level signal is a nicety; losing it must not lose the recording.
    const ctx = await unlockAudio();
    if (ctx && this.stream === stream) {
      try {
        this.meter = new LevelMeter(ctx, ctx.createMediaStreamSource(stream), MIC_LEVEL_OPTIONS);
        this.meter.start();
      } catch (err) {
        console.warn("mic level meter unavailable", err);
        this.meter = null;
      }
    }
  }

  /**
   * Resolves with the recording, or with null when it was too short or too
   * quiet to be speech, so an accidental tap is simply ignored. Always releases
   * the microphone: a recording indicator that never goes off destroys trust.
   */
  stop(): Promise<Blob | null> {
    if (this.pending) return this.pending;
    const recorder = this.recorder;
    if (!recorder) return Promise.resolve(null);

    const durationMs = performance.now() - this.startedAtMs;
    const peak = this.meter?.peak ?? 1; // no meter means no evidence of silence
    const type = recorder.mimeType || this.chosenMime || "audio/webm";

    this.pending = new Promise<Blob | null>((resolve) => {
      const finish = () => {
        const chunks = this.chunks;
        this.teardown();
        this.pending = null;
        if (chunks.length === 0) return resolve(null);
        if (durationMs < MIN_DURATION_MS || peak < MIN_PEAK_LEVEL) return resolve(null);
        resolve(new Blob(chunks, { type }));
      };
      recorder.onstop = finish;
      recorder.onerror = (event) => {
        console.warn("MediaRecorder failed", event);
        finish();
      };
      try {
        if (recorder.state === "inactive") finish();
        else recorder.stop();
      } catch (err) {
        console.warn("MediaRecorder.stop failed", err);
        finish();
      }
    });
    return this.pending;
  }

  /** Abandons a recording in progress and frees the device. */
  cancel(): void {
    const recorder = this.recorder;
    if (recorder && recorder.state !== "inactive") {
      recorder.onstop = null;
      recorder.onerror = null;
      try {
        recorder.stop();
      } catch (err) {
        console.warn("MediaRecorder.stop failed", err);
      }
    }
    this.teardown();
    this.pending = null;
  }

  private teardown(): void {
    this.meter?.dispose();
    this.meter = null;
    if (this.recorder) {
      this.recorder.ondataavailable = null;
      this.recorder.onstop = null;
      this.recorder.onerror = null;
      this.recorder = null;
    }
    if (this.stream) {
      releaseStream(this.stream);
      this.stream = null;
    }
    this.chunks = [];
  }
}

function releaseStream(stream: MediaStream): void {
  for (const track of stream.getTracks()) track.stop();
}

function deviceErrorCode(err: unknown): MicErrorCode {
  const name = err instanceof Error ? err.name : "";
  if (name === "NotAllowedError" || name === "SecurityError") return "denied";
  if (name === "NotFoundError" || name === "OverconstrainedError") return "unavailable";
  if (name === "NotReadableError" || name === "AbortError") return "unavailable";
  return "unavailable";
}

function describe(err: unknown): string {
  return err instanceof Error ? `${err.name}: ${err.message}` : String(err);
}
